/**
 * Newsletter (1.0 admin Newsletter): vista previa (10 productos, destinatarios por idioma), texto
 * en español traducido una vez por idioma al enviar, historial de envíos y reinicio del «ya enviado».
 * Programación (27/10): el mismo texto se envía solo cada día / semana / mes a la hora de Madrid.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { NewsletterPreviewOutput, NewsletterSchedule } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Plan = Pick<NewsletterSchedule, 'enabled' | 'frequency' | 'weekday' | 'monthday' | 'hour'>;
const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export function NewsletterPage(): ReactElement {
  const [preview, setPreview] = useState<NewsletterPreviewOutput | null>(null);
  const [runs, setRuns] = useState<Doc[] | null>(null);
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('Novedades en nuvvu esta semana');
  const [intro, setIntro] = useState('');
  const [outro, setOutro] = useState('');
  const [cta, setCta] = useState('Ver más en nuvvu');
  const [plan, setPlan] = useState<Plan>({
    enabled: false,
    frequency: 'weekly',
    weekday: 1,
    monthday: 1,
    hour: 9,
  });
  const [last, setLast] = useState<Doc | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    call(api.social.previewNewsletter, {})
      .then(setPreview)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
    getDocs(query(collection(db, 'newsletters'), orderBy('created_at', 'desc'), limit(20)))
      .then((s) => {
        setRuns(
          s.docs.filter((d) => !d.id.startsWith('_')).map((d) => ({ id: d.id, ...d.data() })),
        );
      })
      .catch(() => {
        setRuns([]);
      });
  };
  useEffect(load, []);
  useEffect(() => {
    getDoc(doc(db, 'newsletters', '_schedule'))
      .then((d) => {
        const v = d.data();
        if (!v) return;
        const num = (k: string, def: number): number => (typeof v[k] === 'number' ? v[k] : def);
        setPlan({
          enabled: v.enabled === true,
          frequency:
            v.frequency === 'daily' ? 'daily' : v.frequency === 'monthly' ? 'monthly' : 'weekly',
          weekday: num('weekday', 1),
          monthday: num('monthday', 1),
          hour: num('hour', 9),
        });
        if (typeof v.title === 'string') setTitle(v.title);
        setSubject(str(v.subject));
        setIntro(str(v.intro));
        setOutro(str(v.outro));
        if (typeof v.cta === 'string') setCta(v.cta);
        setLast(v);
      })
      .catch(() => undefined);
  }, []);
  const texts = (): {
    title: string;
    subject?: string;
    intro?: string;
    outro?: string;
    cta?: string;
  } => ({
    title: title.trim(),
    ...(subject.trim() ? { subject: subject.trim() } : {}),
    ...(intro.trim() ? { intro: intro.trim() } : {}),
    ...(outro.trim() ? { outro: outro.trim() } : {}),
    ...(cta.trim() ? { cta: cta.trim() } : {}),
  });
  const saveSchedule = (enabled: boolean): void => {
    if (!title.trim()) {
      setError('Escribe al menos el título.');
      return;
    }
    setBusy(true);
    setError(null);
    const next = { ...plan, enabled };
    call(api.social.saveNewsletterSchedule, { ...texts(), ...next })
      .then(() => {
        setPlan(next);
        setNotice(enabled ? `Programada: ${describe(next)}.` : 'Programación desactivada.');
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const send = (): void => {
    if (!preview) return;
    if (
      !window.confirm(
        `¿Enviar la newsletter a ${preview.recipients} destinatarios en ${Object.keys(preview.by_lang).length} idiomas?`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    call(api.social.sendNewsletter, texts())
      .then((r) => {
        setNotice(
          `Enviada: ${r.sent} correos (${r.failed} fallos) · ${r.products} productos · ${r.languages} idiomas.`,
        );
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const reset = (): void => {
    if (!window.confirm('¿Reiniciar el historial de productos enviados?')) return;
    call(api.social.resetNewsletterHistory, {})
      .then(() => {
        setNotice('Historial reiniciado.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Newsletter</h1>
      <p style={muted}>
        5 productos de vendedores PRO/destacados + 5 recientes, nunca repetidos. El texto va en
        español y se traduce una vez por idioma. Solo a usuarios con «marketing» activado. En
        sandbox los correos se redirigen según EMAIL_REDIRECT_TO.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      {preview && (
        <div style={{ ...card, display: 'grid', gap: 8 }}>
          <strong>
            Vista previa: {preview.items.length} productos ({preview.featured_count} PRO/destacados,{' '}
            {preview.recent_count} recientes) · {preview.recipients} destinatarios ·{' '}
            {preview.available} candidatos · {preview.already_sent} ya enviados
          </strong>
          <div style={muted}>
            {Object.entries(preview.by_lang)
              .sort((a, b) => b[1] - a[1])
              .map(([l, n]) => `${l}: ${n}`)
              .join(' · ')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {preview.items.map((p) => (
              <div key={p.id} style={{ width: 120, fontSize: 11 }}>
                {p.photo && (
                  <img
                    src={p.photo}
                    alt=""
                    style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }}
                  />
                )}
                <div>
                  {p.featured ? '⭐ ' : ''}
                  {p.title.slice(0, 40)}
                </div>
                <div style={muted}>
                  {p.price} {p.currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <form
        style={{ ...card, display: 'grid', gap: 8, maxWidth: 640 }}
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Título (español)
          <input
            style={field}
            value={title}
            required
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Asunto (opcional, por defecto el título)
          <input
            style={field}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Introducción
          <textarea
            style={{ ...field, minHeight: 80 }}
            value={intro}
            onChange={(e) => {
              setIntro(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Cierre
          <textarea
            style={{ ...field, minHeight: 60 }}
            value={outro}
            onChange={(e) => {
              setOutro(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Texto del botón
          <input
            style={field}
            value={cta}
            onChange={(e) => {
              setCta(e.target.value);
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="submit"
            style={btn(true)}
            disabled={busy || !preview || preview.items.length < 2}
          >
            Enviar newsletter
          </button>
          <button type="button" style={btn(false, true)} onClick={reset}>
            Reiniciar historial
          </button>
        </div>
      </form>
      <section style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}>
        <strong>Envío automático</strong>
        <span style={muted}>
          Usa el texto de arriba. Cada envío elige 10 productos nuevos (nunca repetidos) y llega a
          cada usuario en su idioma. Si un día no hay productos suficientes, no se envía.
        </span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Frecuencia
            <select
              style={field}
              value={plan.frequency}
              onChange={(e) => {
                setPlan({ ...plan, frequency: e.target.value as Plan['frequency'] });
              }}
            >
              <option value="daily">Cada día</option>
              <option value="weekly">Cada semana</option>
              <option value="monthly">Cada mes</option>
            </select>
          </label>
          {plan.frequency === 'weekly' && (
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Día
              <select
                style={field}
                value={plan.weekday}
                onChange={(e) => {
                  setPlan({ ...plan, weekday: Number(e.target.value) });
                }}
              >
                {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                  <option key={d} value={d}>
                    {WEEKDAYS[d]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {plan.frequency === 'monthly' && (
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Día del mes
              <select
                style={field}
                value={plan.monthday}
                onChange={(e) => {
                  setPlan({ ...plan, monthday: Number(e.target.value) });
                }}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Hora (Madrid)
            <select
              style={field}
              value={plan.hour}
              onChange={(e) => {
                setPlan({ ...plan, hour: Number(e.target.value) });
              }}
            >
              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            style={btn(true)}
            disabled={busy}
            onClick={() => {
              saveSchedule(true);
            }}
          >
            {plan.enabled ? 'Guardar cambios' : 'Activar envío automático'}
          </button>
          {plan.enabled && (
            <button
              type="button"
              style={btn(false, true)}
              disabled={busy}
              onClick={() => {
                saveSchedule(false);
              }}
            >
              Desactivar
            </button>
          )}
          <span style={muted}>
            {plan.enabled ? `Activa: ${describe(plan)}` : 'Desactivada'}
            {typeof last?.last_run_day === 'string' && ` · último intento ${last.last_run_day}`}
            {lastResult(last)}
          </span>
        </div>
      </section>
      <h2 style={{ margin: 0, fontSize: 16 }}>Envíos</h2>
      {runs && (
        <Table
          head={['Fecha', 'Título', 'Destinatarios', 'Enviados', 'Fallos', 'Productos', 'Idiomas']}
        >
          {runs.map((r) => (
            <tr key={str(r.id)}>
              <td style={td}>{when(r.created_at)}</td>
              <td style={td}>{str((r.texts as Doc | undefined)?.title)}</td>
              <td style={td}>{str(r.recipients)}</td>
              <td style={td}>{str(r.sent)}</td>
              <td style={td}>{str(r.failed)}</td>
              <td style={td}>{((r.product_ids as unknown[] | undefined) ?? []).length}</td>
              <td style={td}>{str(r.languages)}</td>
            </tr>
          ))}
          {runs.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Ninguno.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}

function describe(p: Plan): string {
  const at = `a las ${String(p.hour).padStart(2, '0')}:00`;
  if (p.frequency === 'daily') return `cada día ${at}`;
  if (p.frequency === 'weekly') return `cada ${WEEKDAYS[p.weekday] ?? ''} ${at}`;
  return `el día ${String(p.monthday)} de cada mes ${at}`;
}

function lastResult(d: Doc | null): string {
  const r = d?.last_result as Doc | undefined;
  if (!r) return '';
  if (r.ok === true) return ` (enviados ${str(r.sent)})`;
  return r.reason === 'not_enough_products'
    ? ' (sin productos nuevos)'
    : ` (falló: ${str(r.reason)})`;
}
